import { logger } from "../../utils/logger";
import { motion } from 'motion/react';
import { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiConfig';
import * as LucideIcons from 'lucide-react';

const { Rocket, Star, Clock, Award, Shield } = LucideIcons;

interface Metric {
  id: string;
  number: string;
  label: string;
  icon: string;
  order: number;
  color?: string;
}

interface MetricsData {
  id: string;
  visible: boolean;
  badgeText?: string;
  metrics: Metric[];
}

export function SuccessMetricsBar() {
  const [metricsData, setMetricsData] = useState<MetricsData | null>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {

        const response = await fetch(getApiUrl('process-page/metrics'));
        if (response.ok) {
          const result = await response.json();

          if (result.success && result.data) {
            // Transform backend format to frontend format
            const backendData = result.data;
            const transformedData: MetricsData = {
              id: backendData.id || 'metrics-1',
              visible: backendData.visible ?? true,
              // ✅ FIXED: Put 'badge' FIRST (same as ProcessHeroProfessional)
              badgeText: backendData.badge || 
                         backendData.badgeText || 
                         backendData.title || 
                         backendData.sectionTitle ||
                         backendData.heading ||
                         backendData.subtitle ||
                         backendData.description ||
                         'Your Success at the Center of Our Universe',
              metrics: backendData.metrics || []
            };

            setMetricsData(transformedData);
          }
        } else {
          logger.error('❌ [SuccessMetricsBar] Response not OK:', response.status);
        }
      } catch (error) {
        logger.error('❌ [SuccessMetricsBar] Error fetching metrics:', error);
      }
    };
    
    fetchMetrics();
  }, []);

  // Fallback data if backend fails - EXACT ORIGINAL DATA
  const defaultMetrics = [
    { 
      id: '1',
      icon: 'Rocket', 
      number: '650+', 
      label: 'Projects Delivered',
      color: 'from-[#43e97b] to-[#38f9d7]',
      order: 1
    },
    { 
      id: '2',
      icon: 'Star', 
      number: '98%', 
      label: 'Satisfaction Rate',
      color: 'from-[#667eea] to-[#764ba2]',
      order: 2
    },
    { 
      id: '3',
      icon: 'Clock', 
      number: '4-8', 
      label: 'Week Timeline',
      color: 'from-[#E87D3E] to-[#E8B84D]',
      order: 3
    },
    { 
      id: '4',
      icon: 'Award', 
      number: '15+', 
      label: 'Years Experience',
      color: 'from-[#fa709a] to-[#fee140]',
      order: 4
    },
    { 
      id: '5',
      icon: 'Shield', 
      number: 'ISO', 
      label: 'Certified Quality',
      color: 'from-[#4facfe] to-[#00f2fe]',
      order: 5
    }
  ];

  const metrics = metricsData?.metrics || defaultMetrics;
  const visible = metricsData?.visible !== false;
  const badgeText = metricsData?.badgeText || 'Your Success at the Center of Our Universe';

  // Don't render if hidden
  if (!visible) {
    return null;
  }

  // Get icon component dynamically
  const getIconComponent = (iconName: string) => {
    return (LucideIcons as any)[iconName] || Star;
  };

  return (
    <>
      {/* Badge Title */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-2">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-xl border border-white/20 px-5 py-2.5 rounded-full mb-6 shadow-2xl"
          >
            <span className="text-sm font-semibold text-white tracking-wide" style={{ textShadow: '0 0 20px rgba(232, 184, 77, 0.8)' }}>
              {badgeText}
            </span>
          </motion.div>
        </div>
      </div>

      {/* Metrics Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative bg-white/10 backdrop-blur-2xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden"
        >
          {/* Gradient shine effect */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          
          <div className={`grid ${metrics.length <= 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-5'} gap-4 md:gap-6 p-6`}>
            {metrics.map((metric, index) => {
              const Icon = getIconComponent(metric.icon);
              
              return (
                <motion.div
                  key={metric.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="flex flex-col items-center gap-2">
                    {/* Icon with gradient background */}
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${metric.color} flex items-center justify-center mb-1 shadow-lg`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    
                    {/* Value */}
                    <div 
                      className="text-2xl md:text-3xl font-bold text-white"
                      style={{ textShadow: '0 0 20px rgba(255, 255, 255, 0.3)' }}
                    >
                      {metric.number}
                    </div>
                    
                    {/* Label */}
                    <div className="text-xs md:text-sm text-white/80 leading-tight">
                      {metric.label}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </>
  );
}